import { Reference } from "../../shared/refs";

export enum FAULT_TYPE {
  CRITICAL = "CRITICAL",
  MAJOR = "MAJOR",
  MINOR = "MINOR",
  INDETERMINATE = "INDETERMINATE",
  WARNING = "WARNING",
}

export type FaultType = `${FAULT_TYPE}`;

export interface Fault {
  id?: string;
  faultCode?: string | null;
  faultType?: string | null;
  fixLink?: string | null;
  faultText?: string | null;
  isEditable?: boolean | null;
  translations?: Translation[];
}

export interface Translation {
  id?: string;
  languageCode?: string | null;
  languageName?: string | null;
  faultText?: string | null;
  fixLinkLite?: string | null;
  fixLinkExpert?: string | null;
  faultCode?: string | null;
}

/** Payload accepted by the single-fault create and update endpoints. */
export interface FaultPayload {
  faultCode: string;
  faultType: FaultType;
  faultText?: string | null;
  fixLink?: string | null;
  translations: FaultTranslationPayload[];
}

export interface FaultTranslationPayload {
  languageCode: string;
  faultCode?: string | null;
  faultText: string;
  fixLinkLite: string | null;
  fixLinkExpert: string | null;
}

/**
 * The single-fault API guarantees `isEditable`; other fields may be omitted
 * depending on the caller's permissions. IDs are kept broad here because the
 * mapper deliberately normalizes legacy numeric IDs to strings before they
 * reach application state.
 */
export interface FaultResponseDto {
  id?: string | number | null;
  faultCode?: string | null;
  faultType?: string | null;
  fixLink?: string | null;
  faultText?: string | null;
  isEditable: boolean;
  translations?: FaultTranslationResponseDto[] | null;
}

export interface FaultTranslationResponseDto {
  id?: string | number | null;
  languageCode?: string | null;
  languageName?: string | null;
  faultCode?: string | null;
  faultText?: string | null;
  fixLinkLite?: string | null;
  fixLinkExpert?: string | null;
}

/**
 * Drops a blank or null top-level fault text because the single-fault backend
 * rejects `faultText: null`. Translation link keys remain explicit as required
 * by its API contract.
 */
export function toFaultPayload(payload: FaultPayload): FaultPayload {
  const faultPayload: FaultPayload = {
    faultCode: payload.faultCode,
    faultType: payload.faultType,
    translations: payload.translations.map((translation) => {
      const translationPayload: FaultTranslationPayload = {
        languageCode: translation.languageCode,
        faultText: translation.faultText,
        fixLinkLite: translation.fixLinkLite ?? null,
        fixLinkExpert: translation.fixLinkExpert ?? null,
      };

      if (translation.faultCode !== undefined) {
        translationPayload.faultCode = translation.faultCode;
      }

      return translationPayload;
    }),
  };

  if (payload.fixLink !== undefined) {
    faultPayload.fixLink = payload.fixLink;
  }

  if (typeof payload.faultText === "string" && payload.faultText.trim().length > 0) {
    faultPayload.faultText = payload.faultText;
  }

  return faultPayload;
}

/** Maps a single-fault response into the frontend's defensive Fault model. */
export function mapFaultResponse(response: FaultResponseDto | null | undefined): Fault {
  const faultResponse: Partial<FaultResponseDto> = response ?? {};
  const fault: Fault = {};

  const id = toStringId(faultResponse.id);
  const faultCode = toNullableString(faultResponse.faultCode);
  const faultType = toNullableString(faultResponse.faultType);
  const faultText = toNullableString(faultResponse.faultText);
  const fixLink = toNullableString(faultResponse.fixLink);

  if (id !== undefined) fault.id = id;
  if (faultCode !== undefined) fault.faultCode = faultCode;
  if (faultType !== undefined) fault.faultType = faultType;
  if (faultText !== undefined) fault.faultText = faultText;
  if (fixLink !== undefined) fault.fixLink = fixLink;
  // A missing or malformed value must never enable fault mutations.
  fault.isEditable = faultResponse.isEditable === true;

  if (Array.isArray(faultResponse.translations)) {
    fault.translations = faultResponse.translations.map(mapFaultTranslationResponse);
  }

  return fault;
}

function mapFaultTranslationResponse(response: FaultTranslationResponseDto): Translation {
  const translation: Translation = {};

  const id = toStringId(response.id);
  const languageCode = toNullableString(response.languageCode);
  const languageName = toNullableString(response.languageName);
  const faultCode = toNullableString(response.faultCode);
  const faultText = toNullableString(response.faultText);
  const fixLinkLite = toNullableString(response.fixLinkLite);
  const fixLinkExpert = toNullableString(response.fixLinkExpert);

  if (id !== undefined) translation.id = id;
  if (languageCode !== undefined) translation.languageCode = languageCode;
  if (languageName !== undefined) translation.languageName = languageName;
  if (faultCode !== undefined) translation.faultCode = faultCode;
  if (faultText !== undefined) translation.faultText = faultText;
  if (fixLinkLite !== undefined) translation.fixLinkLite = fixLinkLite;
  if (fixLinkExpert !== undefined) translation.fixLinkExpert = fixLinkExpert;

  return translation;
}

function toStringId(value: string | number | null | undefined): string | undefined {
  if (typeof value === "string") {
    return value;
  } else if (typeof value === "number" && Number.isFinite(value)) {
    return String(value);
  }

  return undefined;
}

function toNullableString(value: string | null | undefined): string | null | undefined {
  if (typeof value === "string" || value === null) {
    return value;
  }

  return undefined;
}

export interface WikiFault extends Fault {
  manufacturers?: Reference[];
  models?: Reference[];
  faultList?: string;
}
